import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Payment.css";
import LEGAL_CONFIG from "../config/legalConfig";
import { useNavbar } from "../hooks/useNavbar";
import { PromptPayQR, usePromptPayConfig } from "../utils/promptpay";
import { useLanguage } from "../context/LanguageContext";
import { useBookingCart, MAX_GROUP_ROOMS } from "../context/BookingCartContext";
import { t } from "../i18n";
import Toast from "../components/Toast";
import API from "../config";

/* มัดจำล็อคไว้ที่ 50% ของยอดรวม — ลูกค้าเลือกเปอร์เซ็นต์เองไม่ได้ */
const PARTIAL_PCT = 50;

/* ─────────────────────────────────────────
   PromptPay QR Section
───────────────────────────────────────────── */
function PromptPaySection({ payAmount, payType, lang }) {
  const [copied, setCopied] = useState(false);
  const promptpayConfig = usePromptPayConfig();

  const handleCopy = () => {
    navigator.clipboard.writeText(promptpayConfig.phoneNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="promptpay-section">
      <div className="promptpay-body">
        <div className="qr-wrapper">
          <div className="qr-inner">
            <PromptPayQR amount={payAmount} />
          </div>
          <div className="qr-scan-hint">{t(lang, "scanToTransfer")}</div>
        </div>
        <div className="promptpay-info">
          <div className="pp-info-row">
            <span className="pp-info-label">{t(lang, "accountNameLabel")}</span>
            <span className="pp-info-value">{promptpayConfig.accountName}</span>
          </div>
          <div className="pp-info-row">
            <span className="pp-info-label">{t(lang, "bankLabel")}</span>
            <span className="pp-info-value">{promptpayConfig.bankName}</span>
          </div>
          <div className="pp-info-row">
            <span className="pp-info-label">{t(lang, "promptPayLabel")}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="pp-info-value pp-phone">{promptpayConfig.phoneNumber}</span>
              <button className={"pp-copy-btn" + (copied ? " copied" : "")} onClick={handleCopy}>
                {copied ? t(lang, "copied") : t(lang, "copyBtn")}
              </button>
            </div>
          </div>
          <div className="pp-info-row pp-amount-row">
            <span className="pp-info-label">{t(lang, "amountLabel")}</span>
            <span className="pp-info-value pp-amount-value">
              {Number(payAmount).toLocaleString()} THB
              {payType === "partial" && <span className="pp-partial-tag">{t(lang, "depositTag")}</span>}
            </span>
          </div>
          <div className="pp-notice">
            <span>📌</span>
            <span>{t(lang, "uploadSlipAfter")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function buildText(doc) {
  return doc.sections.map(s => s.heading + "\n" + s.body).join("\n\n");
}

function TermsModal({ title, content, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
      onClick={onClose}>
      <div style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)",
        borderRadius: "20px", width: "100%", maxWidth: "500px", maxHeight: "80vh",
        display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        fontFamily: "'Noto Sans Thai', sans-serif" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(0,0,0,0.1)",
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: "#111", fontSize: "16px" }}>{title}</h5>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px",
            cursor: "pointer", color: "#999", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, fontSize: "13px",
          color: "#444", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>{content}</div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.1)",
          display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 24px", borderRadius: "20px", border: "none",
            background: "#111", cursor: "pointer", fontSize: "13px", color: "#fff", fontWeight: 600 }}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
}

function nowThaiTime() {
  const now = new Date();
  const thai = new Date(now.getTime() + 7 * 60 * 60 * 1000);
  return thai.toISOString().replace("Z", "+07:00");
}

function BookingSuccessPopup({ data, room, items = [], checkIn, checkOut, nights, payAmount, remaining, payType, cashApproved, onClose, lang, roomsCount }) {
  const isMulti = items.length > 1;
  const isCash = payType === "cash";
  /* ไม่โชว์เลขห้องในหน้ายืนยัน — ห้องยังถูกสลับได้ก่อนเช็คอิน บอกไปแล้วลูกค้าจะยึดเลขนั้น
     (เหตุผลเดียวกับอีเมลยืนยัน ดู backend/server.js ตรง sendBookingConfirmedEmail)
     data.roomNumbers ยังส่งมาจาก backend เหมือนเดิม แค่ไม่เอามาแสดง */
  const bookedQty = data.roomsCount || roomsCount || 1;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("th-TH", {
    year: "numeric", month: "long", day: "numeric"
  }) : "-";

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-card" onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-check">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3>{t(lang, "bookingSuccessTitle")}</h3>
          {/* เงินสดอนุมัติทันทีเฉพาะตอนพนักงานจองแทน ลูกค้าจองเองต้องรอเจ้าหน้าที่ยืนยัน */}
          <p>{isCash
            ? t(lang, cashApproved ? "cashApprovedSub" : "cashPendingSub")
            : t(lang, "waitingSlipVerify")}</p>
        </div>
        {/* จองหลายประเภท — แบนเนอร์รูปเดียวโชว์ได้แค่ประเภทแรก ใช้ลิสต์แทน */}
        {isMulti ? (
          <div className="popup-room-list">
            {items.map(({ room: r, qty }) => (
              <div className="popup-room-list-row" key={r._id}>
                <span>{lang === "en" && r.nameEn ? r.nameEn : r.name}</span>
                <b>× {qty}</b>
              </div>
            ))}
          </div>
        ) : (
          <div className="popup-room-banner">
            {room.image
              ? <img src={`/uploads/${room.image}`} alt={lang === "en" && room.nameEn ? room.nameEn : room.name} />
              : <div style={{ width: "100%", height: 140, background: "#e5e7eb",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 36 }}>🛏️</div>}
            <div className="popup-room-overlay">
              <span className="popup-room-name">{lang === "en" && room.nameEn ? room.nameEn : room.name}</span>
            </div>
          </div>
        )}
        <div className="popup-body">
          <div className="popup-grid">
            <div className="popup-item">
              <span className="popup-label">📅 {t(lang, "popupCheckIn")}</span>
              <span className="popup-value">{fmt(checkIn)}</span>
            </div>
            <div className="popup-item">
              <span className="popup-label">📅 {t(lang, "popupCheckOut")}</span>
              <span className="popup-value">{fmt(checkOut)}</span>
            </div>
            <div className="popup-item">
              <span className="popup-label">🌙 {t(lang, "popupNights")}</span>
              <span className="popup-value">{nights} {t(lang, "nightsUnit2")}</span>
            </div>
            {bookedQty > 1 && (
              <div className="popup-item">
                <span className="popup-label">🛏️ {t(lang, "popupRooms")}</span>
                <span className="popup-value">{bookedQty} {t(lang, "roomsUnit")}</span>
              </div>
            )}
            <div className="popup-item">
              <span className="popup-label">📌 {t(lang, "popupStatus")}</span>
              <span className={`popup-badge${isCash && cashApproved ? " ok" : ""}`}>
                {isCash && cashApproved ? t(lang, "cashApprovedBadge") : t(lang, "pendingReview")}
              </span>
            </div>
            <div className="popup-item">
              <span className="popup-label">💳 {t(lang, "paidPopup")}</span>
              <span className="popup-value popup-green">
                {payType === "cash" ? t(lang, "payAtCounter") : `${Number(payAmount).toLocaleString()} THB`}
                {payType === "partial" && <small> ({t(lang, "partialTag")} {PARTIAL_PCT}%)</small>}
              </span>
            </div>
            {remaining > 0 && (
              <div className="popup-item">
                <span className="popup-label">⏳ {isCash ? t(lang, "amountDue") : t(lang, "remainingBalance")}</span>
                <span className="popup-value popup-orange">{Number(remaining).toLocaleString()} THB</span>
              </div>
            )}
          </div>
          <p className="popup-note">
            {isCash
              ? `💵 ${t(lang, cashApproved ? "cashApprovedNote" : "cashPendingNote")}`
              : `📬 ${t(lang, "adminVerify24h")}`}
          </p>
          {data.isNewUser && (
            <p className="popup-note" style={{ color: "#16a34a", marginTop: 8 }}>
              🎉 {t(lang, "newAccountNote")}
            </p>
          )}
        </div>
        <div className="popup-footer">
          <button className="popup-btn-ok" onClick={onClose}>{t(lang, "acknowledge")}</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Main Payment Component
───────────────────────────────────────────── */
function Payment() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── เลือก Navbar ตาม role ──────────────────────────────────────
  const NavbarComponent = useNavbar();
  const { lang } = useLanguage();
  const cart = useBookingCart();

  const st         = location.state || {};
  const checkIn    = st.checkIn;
  const checkOut   = st.checkOut;
  const nights     = st.nights || 1;

  /* รวมทางเข้าสองแบบให้เป็น items เดียว แล้วโค้ดที่เหลือทั้งหน้าอ้าง room/roomsCount ได้เหมือนเดิม
       - ตะกร้าจากหน้า Rooms   → st.items = [{room, qty}] หลายประเภทได้
       - ปุ่มจองเลยใน RoomDetail → st.room + st.roomsCount (ทรงเดิม)
     ใช้ location.state ไม่ใช่ cart context เพราะ React Router เก็บใน history.state → รอด hard reload
     clamp ราย item ไม่ใช่ยอดรวม ไม่งั้นตะกร้า 3+4+5 จะกลายเป็น 10 เงียบๆ */
  const items = useMemo(() => {
    const clamp = n => Math.min(MAX_GROUP_ROOMS, Math.max(1, Number(n) || 1));
    if (Array.isArray(st.items) && st.items.length)
      return st.items.filter(i => i?.room?._id).map(i => ({ room: i.room, qty: clamp(i.qty) }));
    if (st.room) return [{ room: st.room, qty: clamp(st.roomsCount) }];
    return [];
  }, [st]);

  const room       = items[0]?.room;
  const roomsCount = items.reduce((s, i) => s + i.qty, 0) || 1;
  const isMulti    = items.length > 1;
  const totalPrice = st.totalPrice
    || items.reduce((sum, i) => sum + (i.room.price || 0) * i.qty * nights, 0);

  const [token]    = useState(() => localStorage.getItem("token"));
  const isLoggedIn = !!token;

  const [userRole,       setUserRole]       = useState("");
  const [profileLoading, setProfileLoading] = useState(true);

  const isStaff      = userRole === "staff";
  const isAdmin      = userRole === "admin";
  const isPrivileged = isStaff || isAdmin;

  const [firstName,        setFirstName]        = useState("");
  const [lastName,         setLastName]         = useState("");
  const [email,            setEmail]            = useState("");
  const [phone,            setPhone]            = useState("");
  const [payType,          setPayType]          = useState("partial");
  const [slipFile,         setSlipFile]         = useState(null);
  const [preview,          setPreview]          = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [successData,      setSuccessData]      = useState(null);
  const [consentChecked,   setConsentChecked]   = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);
  const [modal,            setModal]            = useState(null);
  const [toasting,         setToasting]         = useState(null);

  const toast = (msg, type = "error") => {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 3000);
  };

  // fetch role — useNavbar ก็ fetch แล้ว แต่ Payment ต้องการ role สำหรับ logic อื่นด้วย
  useEffect(() => {
    if (!isLoggedIn) { setProfileLoading(false); return; }
    fetch(`${API}/api/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        const role = data.role || "";
        setUserRole(role);
        if (!["staff", "admin"].includes(role) && data.email) setEmail(data.email);
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [isLoggedIn, token]);

  const handleConsentChange = (e) => {
    const checked = e.target.checked;
    setConsentChecked(checked);
    setConsentTimestamp(checked ? nowThaiTime() : null);
  };

  if (!items.length) return (
    <div className="pay-empty">
      <h3>{t(lang, "roomNotFound")}</h3>
      <button onClick={() => navigate("/rooms")}>{t(lang, "backToRooms")}</button>
    </div>
  );

  if (profileLoading) return (
    <div className="pay-page">
      <NavbarComponent />
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <div className="rd-spinner" />
      </div>
    </div>
  );

  /* cash = ยังไม่จ่ายตอนนี้ ไปชำระที่เคาน์เตอร์ทั้งจำนวน */
  const payAmount = (() => {
    if (payType === "full") return totalPrice;
    if (payType === "cash") return 0;
    return Math.round(totalPrice * PARTIAL_PCT / 100);
  })();
  const remaining = totalPrice - payAmount;

  const fmt = (d) => d ? new Date(d).toLocaleDateString("th-TH", {
    year: "numeric", month: "long", day: "numeric"
  }) : "-";

  const handleSlipChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    /* validate ชนิด + ขนาดไฟล์ ตามที่ UI บอกไว้ */
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast("รองรับเฉพาะไฟล์ภาพ PNG / JPG เท่านั้น");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast("ขนาดไฟล์ต้องไม่เกิน 5MB");
      e.target.value = "";
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setSlipFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_RE = /^0\d{8,9}$/;

  const handleSubmit = async () => {
    if ((!isLoggedIn || isPrivileged) && !firstName) return toast("กรุณากรอกชื่อ");
    if ((!isLoggedIn || isPrivileged) && !lastName)  return toast("กรุณากรอกนามสกุล");
    /* แอดมิน/สตาฟจองแทนลูกค้า: อีเมล+เบอร์ไม่บังคับ แต่ถ้ากรอกมาต้องถูกรูปแบบ */
    if (!isPrivileged && !email)                      return toast("กรุณากรอก Email");
    if (email && !EMAIL_RE.test(email.trim()))        return toast("รูปแบบอีเมลไม่ถูกต้อง");
    if (!isPrivileged && !phone)                      return toast("กรุณากรอกเบอร์โทรศัพท์");
    if (phone && !PHONE_RE.test(phone.replace(/[-\s]/g, ""))) return toast("เบอร์โทรไม่ถูกต้อง (ตัวเลข 9-10 หลัก ขึ้นต้นด้วย 0)");
    if (!isLoggedIn && !consentChecked)               return toast("กรุณายอมรับเงื่อนไขการให้บริการก่อน");
    if (!isPrivileged && payType !== "cash" && !slipFile) return toast("กรุณาอัปโหลดสลิปการชำระเงิน");
    if (!checkIn || !checkOut)                        return toast("ไม่พบวันที่เช็คอิน/เช็คเอาต์");

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("roomId",     room._id);
      formData.append("roomTypeId", room._id);
      formData.append("roomsCount", roomsCount);
      // ตะกร้าหลายประเภท — backend ไม่มี items ก็ fallback ไปใช้ roomTypeId+roomsCount แบบเดิม
      formData.append("items", JSON.stringify(items.map(i => ({ roomTypeId: i.room._id, qty: i.qty }))));
      formData.append("firstName",  firstName);
      formData.append("lastName",   lastName);
      formData.append("email",      email);
      formData.append("phone",      phone);
      formData.append("amount",     payAmount);
      formData.append("remaining",  remaining);
      formData.append("totalPrice", totalPrice);
      formData.append("payType",    payType);
      formData.append("checkIn",    checkIn);
      formData.append("checkOut",   checkOut);
      if (isPrivileged) formData.append("bookedByStaff", "true");
      if (!isLoggedIn && consentTimestamp) formData.append("consentAcceptedAt", consentTimestamp);
      if (isPrivileged) formData.append("consentAcceptedAt", nowThaiTime());
      if (slipFile) formData.append("slip", slipFile);

      const res = await fetch(`${API}/api/payment`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { toast(data.message || "การชำระเงินไม่สำเร็จ"); return; }
      cart?.clear();          // จองสำเร็จแล้ว ห้ามให้ตะกร้าเดิมค้างไปจองซ้ำ
      setSuccessData(data);
    } catch (err) {
      console.error(err);
      toast("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    } finally {
      setLoading(false);
    }
  };

  // หลัง success — staff/admin กลับหน้า booking ของตัวเอง
  const successRedirect = isAdmin ? "/admin/booking" : isStaff ? "/booking" : "/rooms";

  const submitDisabled = loading || (!isLoggedIn && !consentChecked);
  const showQR = payType !== "cash" && !isPrivileged;

  const PrivilegedBadge = () => (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      background: isAdmin ? "#faf5ff" : "#eff6ff",
      border: `1px solid ${isAdmin ? "#d8b4fe" : "#bfdbfe"}`,
      borderRadius: "20px", padding: "4px 12px",
      fontSize: "12px", fontWeight: 700,
      color: isAdmin ? "#7c3aed" : "#1d4ed8",
      marginBottom: "14px"
    }}>
      {isAdmin ? "👑 จองในนามแอดมิน (Admin Mode)" : "🏷️ จองในนามพนักงาน (Staff Mode)"}
    </div>
  );

  return (
    <div className="pay-page">
      {/* ── Navbar เลือกตาม role อัตโนมัติ ── */}
      <NavbarComponent />

      {modal === "terms"   && <TermsModal title={LEGAL_CONFIG.terms.title}   content={buildText(LEGAL_CONFIG.terms)}   onClose={() => setModal(null)} />}
      {modal === "privacy" && <TermsModal title={LEGAL_CONFIG.privacy.title} content={buildText(LEGAL_CONFIG.privacy)} onClose={() => setModal(null)} />}

      {successData && (
        <BookingSuccessPopup
          data={successData} room={room} items={items}
          checkIn={checkIn} checkOut={checkOut}
          nights={nights} payAmount={payAmount}
          remaining={remaining} payType={payType}
          cashApproved={payType === "cash" && isPrivileged}
          onClose={() => navigate(successRedirect)}
          lang={lang}
          roomsCount={roomsCount}
        />
      )}

      <div className="pay-wrapper">
        <div className="pay-box">

          {/* หัวเรื่อง
              จองหลายประเภท: ห้ามเอารูป+ชื่อของประเภทแรกมาขึ้นเป็นหัวเรื่อง เพราะอ่านเหมือนว่า
              ทั้งการจองเป็นห้องประเภทนั้น — ใช้สรุประดับการจองแทน แล้วแจกแจงในบล็อกรายการข้างล่าง */}
          <div className="pay-room-header">
            {isMulti ? (
              <div className="pay-room-thumb pay-thumb-multi">🛏️</div>
            ) : room.image ? (
              <img src={`/uploads/${room.image}`} alt={lang === "en" && room.nameEn ? room.nameEn : room.name} className="pay-room-thumb" />
            ) : (
              <div className="pay-room-thumb pay-thumb-multi">🛏️</div>
            )}
            <div className="pay-room-meta">
              <h3>{isMulti
                ? t(lang, "payMultiHeading", roomsCount, items.length)
                : (lang === "en" && room.nameEn ? room.nameEn : room.name)}</h3>
              <div className="pay-room-dates">
                <span>📅 {fmt(checkIn)}</span>
                <span className="pay-arrow">→</span>
                <span>📅 {fmt(checkOut)}</span>
              </div>
              <span className="pay-nights-badge">{nights} {t(lang, "nightsUnit2")}</span>
              {/* จองหลายประเภทมี "N ห้อง" อยู่ในหัวเรื่องแล้ว ไม่ต้องซ้ำเป็น badge */}
              {!isMulti && roomsCount > 1 && (
                <span className="pay-nights-badge" style={{ marginLeft: 6, background: "#eef2ff", color: "#3730a3" }}>
                  🛏️ {roomsCount} {t(lang, "roomsUnit")}
                </span>
              )}
            </div>
          </div>

          {/* บล็อกรายการห้อง — มีหัวข้อกำกับ + ยอดรวมย่อยปิดท้าย
              จะได้เห็นยอดตั้งแต่ตรงนี้ ไม่ต้องเลื่อนลงไปดูสรุปท้ายหน้า */}
          {isMulti && (
            <div className="pay-items">
              <div className="pay-items-title">{t(lang, "payRoomsTitle")}</div>
              {items.map(({ room: r, qty }) => (
                <div className="pay-item-row" key={r._id}>
                  <div className="pay-item-thumb">
                    {r.image
                      ? <img src={`/uploads/${r.image}`} alt={lang === "en" && r.nameEn ? r.nameEn : r.name} />
                      : <span>🛏️</span>}
                  </div>
                  <div className="pay-item-meta">
                    <div className="pay-item-name">
                      {/* ต้องห่อ span — ชื่อเป็น text node เปล่าๆ จะ truncate ด้วย CSS ไม่ได้ */}
                      <span className="pay-item-label">{lang === "en" && r.nameEn ? r.nameEn : r.name}</span>
                      <span className="pay-item-qty">{t(lang, "payQtyChip", qty)}</span>
                    </div>
                    <div className="pay-item-calc">
                      {r.price?.toLocaleString()} THB × {nights} {t(lang, "nightsUnit2")}
                    </div>
                  </div>
                  <div className="pay-item-sub">{((r.price || 0) * qty * nights).toLocaleString()} THB</div>
                </div>
              ))}
              <div className="pay-items-total">
                <span>{t(lang, "cartTotal")}</span>
                <strong>{totalPrice.toLocaleString()} THB</strong>
              </div>
            </div>
          )}

          <div className="pay-divider" />

          {/* ══ Contact ══ */}
          <div className="pay-section">
            <h4 className="pay-section-title">{t(lang, "guestInfoTitle")}</h4>

            {isPrivileged && <PrivilegedBadge />}

            {(!isLoggedIn || isPrivileged) && (
              <div style={{ display: "flex", gap: "12px" }}>
                <div className="pay-field" style={{ flex: 1 }}>
                  <label>{t(lang, "firstNameLabel")} <span className="req">*</span></label>
                  <input type="text" placeholder={isPrivileged ? t(lang, "customerFirstPh") : t(lang, "firstNameLabel")}
                    value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div className="pay-field" style={{ flex: 1 }}>
                  <label>{t(lang, "lastNameLabel")} <span className="req">*</span></label>
                  <input type="text" placeholder={isPrivileged ? t(lang, "customerLastPh") : t(lang, "lastNameLabel")}
                    value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
            )}

            <div className="pay-field">
              {/* แอดมิน/สตาฟจองให้ลูกค้าหน้าเคาน์เตอร์ อาจไม่มีอีเมล/เบอร์ให้ — ไม่บังคับ */}
              <label>
                {t(lang, "emailLabel")}{" "}
                {isPrivileged
                  ? <span className="pay-optional">({t(lang, "optionalTag")})</span>
                  : <span className="req">*</span>}
              </label>
              <input type="email" placeholder={isPrivileged ? t(lang, "customerEmailPh") : "example@email.com"}
                value={email} onChange={e => setEmail(e.target.value)}
                readOnly={isLoggedIn && !isPrivileged}
                style={isLoggedIn && !isPrivileged
                  ? { background: "#f0f0f0", color: "#888", cursor: "not-allowed" } : {}} />
            </div>

            <div className="pay-field">
              <label>
                {t(lang, "phoneNumLabel")}{" "}
                {isPrivileged
                  ? <span className="pay-optional">({t(lang, "optionalTag")})</span>
                  : <span className="req">*</span>}
              </label>
              <input type="tel" placeholder={isPrivileged ? t(lang, "customerPhonePh") : "08X-XXX-XXXX"}
                value={phone} maxLength={12}
                onChange={e => setPhone(e.target.value.replace(/[^0-9-\s]/g, ""))} />
            </div>

            {!isLoggedIn && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginTop: "14px" }}>
                <input type="checkbox" id="pay-consent" checked={consentChecked}
                  onChange={handleConsentChange}
                  style={{ width: "18px", height: "18px", marginTop: "2px", border: "2px solid #333",
                    borderRadius: "4px", cursor: "pointer", accentColor: "#333", flexShrink: 0 }} />
                <label htmlFor="pay-consent" style={{ fontSize: "13px", color: "#444", lineHeight: "1.5" }}>
                  {t(lang, "agreeToThe")}{" "}
                  <button type="button" onClick={() => setModal("terms")}
                    style={{ background: "none", border: "none", padding: 0, color: "#1a237e",
                      fontWeight: 600, cursor: "pointer", fontSize: "13px", textDecoration: "underline" }}>
                    {t(lang, "termsOfUse")}
                  </button>
                  {" "}{t(lang, "andWord")}{" "}
                  <button type="button" onClick={() => setModal("privacy")}
                    style={{ background: "none", border: "none", padding: 0, color: "#1a237e",
                      fontWeight: 600, cursor: "pointer", fontSize: "13px", textDecoration: "underline" }}>
                    {t(lang, "privacyPol")}
                  </button>
                  {" "}ของ EURNGKUM Guest House
                </label>
              </div>
            )}

            {isPrivileged && (
              <div style={{ marginTop: "14px", background: isAdmin ? "#faf5ff" : "#f0f9ff",
                border: `1px solid ${isAdmin ? "#d8b4fe" : "#bae6fd"}`,
                borderRadius: "10px", padding: "10px 14px", fontSize: "12px",
                color: isAdmin ? "#7c3aed" : "#0369a1", lineHeight: "1.6" }}>
                {/* ไม่กรอกอีเมล = ไม่มีที่ส่ง QR อย่าไปสัญญาว่าจะส่งให้ */}
                {(isAdmin ? "👑 จองโดยแอดมิน" : "💼 จองโดยพนักงาน") + " — ไม่ต้องแนบสลิป "}
                {email.trim()
                  ? "ระบบจะส่ง QR PromptPay ให้ลูกค้าทางเมล"
                  : "(ไม่ได้กรอกอีเมล ระบบจะไม่ส่ง QR ให้ลูกค้า — ต้องเก็บเงินที่เคาน์เตอร์)"}
              </div>
            )}
          </div>

          <div className="pay-divider" />

          {/* ══ Payment Type ══ */}
          <div className="pay-section">
            <h4 className="pay-section-title">{t(lang, "paymentMethodTitle")}</h4>
            <div className="pay-options">
              <label className={`pay-option ${payType === "full" ? "selected" : ""}`}>
                <input type="radio" value="full" checked={payType === "full"}
                  onChange={e => setPayType(e.target.value)} />
                <div className="opt-icon">💳</div>
                <div>
                  <div className="opt-label">{t(lang, "payFull")}</div>
                  <div className="opt-amount">{totalPrice.toLocaleString()} THB</div>
                </div>
              </label>
              <label className={`pay-option ${payType === "partial" ? "selected" : ""}`}>
                <input type="radio" value="partial" checked={payType === "partial"}
                  onChange={e => setPayType(e.target.value)} />
                <div className="opt-icon">✂️</div>
                <div>
                  <div className="opt-label">{t(lang, "payPartial")}</div>
                  <div className="opt-amount">{Math.round(totalPrice * PARTIAL_PCT / 100).toLocaleString()} THB</div>
                </div>
              </label>
              <label className={`pay-option ${payType === "cash" ? "selected" : ""}`}>
                <input type="radio" value="cash" checked={payType === "cash"}
                  onChange={e => setPayType(e.target.value)} />
                <div className="opt-icon">💵</div>
                <div>
                  <div className="opt-label">{t(lang, "payCash")}</div>
                  <div className="opt-amount">{t(lang, "payAtCounter")}</div>
                </div>
              </label>
            </div>

            {payType === "partial" && (
              <div className="partial-box">
                <div className="partial-fixed-row">
                  <span className="partial-fixed-pct">{PARTIAL_PCT}%</span>
                  <span className="partial-fixed-amount">
                    = <b>{payAmount.toLocaleString()} THB</b>
                  </span>
                </div>
                <div className="partial-fixed-note">🔒 {t(lang, "payPartialNote")}</div>
              </div>
            )}

            {payType === "cash" && (
              <div className="cash-note">
                💡 {t(lang, "cashNote")}
              </div>
            )}
          </div>

          {/* ══ PromptPay QR (full / partial, ไม่ใช่ privileged) ══ */}
          {showQR && (
            <>
              <div className="pay-divider" />
              <div className="pay-section" style={{ paddingBottom: 0 }}>
                <h4 className="pay-section-title">{t(lang, "paymentChannelTitle")}</h4>
              </div>
              <PromptPaySection payAmount={payAmount} payType={payType} lang={lang} />
            </>
          )}

          {/* ── Staff/Admin: QR ส่งทางเมล ── */}
          {payType !== "cash" && isPrivileged && (
            <>
              <div className="pay-divider" />
              <div className="pay-section">
                <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd",
                  borderRadius: "12px", padding: "14px 16px",
                  display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <span style={{ fontSize: "20px", lineHeight: 1 }}>📱</span>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#0369a1", marginBottom: "4px" }}>
                      ระบบจะส่ง QR Code PromptPay ให้ลูกค้าอัตโนมัติ
                    </div>
                    <div style={{ fontSize: "12px", color: "#0c4a6e", lineHeight: "1.6" }}>
                      ลูกค้าจะได้รับอีเมลพร้อม QR Code สำหรับโอนเงิน{" "}
                      <b>{remaining > 0 ? `${remaining.toLocaleString()} THB` : `${payAmount.toLocaleString()} THB`}</b>
                      {" "}— จากนั้นพนักงานสามารถเพิ่มสลิปได้ภายหลังในหน้าตรวจสอบสลิป
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="pay-divider" />

          {/* ══ Slip Upload ══ */}
          {payType !== "cash" && !isPrivileged && (
            <div className="pay-section">
              <h4 className="pay-section-title">{t(lang, "uploadSlipTitle")} <span className="req">*</span></h4>
              <label className="slip-upload-area">
                {preview
                  ? <img src={preview} alt="slip" className="slip-preview-img" />
                  : <div className="slip-placeholder">
                      <span className="slip-icon">📎</span>
                      <span>{t(lang, "clickToSelectSlip")}</span>
                      <span className="slip-hint">{t(lang, "slipFileHint")}</span>
                    </div>
                }
                <input type="file" accept="image/*" onChange={handleSlipChange} hidden />
              </label>
              {preview && (
                <button className="slip-change-btn" onClick={() => { setPreview(null); setSlipFile(null); }}>
                  {t(lang, "changeSlip")}
                </button>
              )}
            </div>
          )}

          <div className="pay-divider" />

          {/* ══ Summary ══ */}
          <div className="pay-summary">
            {items.map(({ room: r, qty }) => (
              <div className="sum-row" key={r._id}>
                <span>{qty > 1
                  ? t(lang, "sumLineRooms", r.price?.toLocaleString(), nights, qty)
                  : t(lang, "sumLineOne",   r.price?.toLocaleString(), nights)}
                  {isMulti && <small style={{ color: "#9ca3af" }}> · {lang === "en" && r.nameEn ? r.nameEn : r.name}</small>}
                </span>
                <span>{((r.price || 0) * qty * nights).toLocaleString()} THB</span>
              </div>
            ))}
            {isMulti && (
              <div className="sum-row">
                <span><b>{t(lang, "grandTotal")}</b></span>
                <span><b>{totalPrice.toLocaleString()} THB</b></span>
              </div>
            )}
            <div className="sum-row pay-now">
              <span>{t(lang, "payNow")}</span>
              <span>{payType === "cash" ? `— (${t(lang, "payAtCounter")})` : `${payAmount.toLocaleString()} THB`}</span>
            </div>
            {remaining > 0 && (
              <div className="sum-row remaining">
                <span>{payType === "cash" ? t(lang, "amountAtCounter") : t(lang, "remainingDue")}</span>
                <span>{remaining.toLocaleString()} THB</span>
              </div>
            )}
          </div>

          <button className="pay-submit-btn" onClick={handleSubmit} disabled={submitDisabled}
            style={isAdmin ? { background: "#7c3aed" } : isStaff ? { background: "#1d4ed8" } : {}}>
            {loading
              ? t(lang, "submitting")
              : isAdmin
                ? `👑 ยืนยันการจองแทนลูกค้า ${payAmount.toLocaleString()} THB →`
                : isStaff
                  ? `✅ ยืนยันการจองแทนลูกค้า ${payAmount.toLocaleString()} THB →`
                  : payType === "cash"
                    ? "ยืนยันการจอง (ชำระที่เคาน์เตอร์) →"
                    : `ยืนยันการชำระ ${payAmount.toLocaleString()} THB →`
            }
          </button>

        </div>
      </div>

      {/* Toast */}
      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </div>
  );
}

export default Payment;